// "What am I looking at?" — the plain-words explanation behind a module's ? button (Ben,
// 2026-10-17: "describe in very basic terms exactly what I am currently looking at"). Built from
// the module's own settings, so it's instant and always matches the screen. Pure; one function per
// module type (the Stats tile first). Neutral wording: no he/she.

import {
  isExcludeValue,
  selectedValuesOf,
  type ActiveFilters,
  type CategoricalActive,
  type RangeActive,
} from "../engines/stats-filter-kit.js";
import { isExcludeValue as isEventExclude, type EventActive, type EventFilterDef } from "../engines/event-map-kit.js";

/** "A", "A and B", "A, B and C". */
export function listWords(items: readonly string[], joiner = "and"): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} ${joiner} ${items[items.length - 1]}`;
}

/** A value chip in words: "Midfielder or Full Back", or "anything but Spain". */
function valuesWords(v: CategoricalActive | RangeActive | undefined): string | null {
  if (v === undefined) return null;
  if (isExcludeValue(v)) return `anything but ${v.value}`;
  const vs = selectedValuesOf(v as CategoricalActive);
  return vs.length ? listWords(vs, "or") : null;
}

/** The filters (other than League and Season) as phrases: "who play as Midfielder", …
 *  `playerName` = whose age the Age chip is measured from. */
export function filterPhrases(filters: ActiveFilters, playerName: string): string[] {
  const out: string[] = [];
  const position = valuesWords(filters.position);
  if (position) out.push(`who play as ${position} (their minutes there)`);
  const nationality = valuesWords(filters.nationality);
  if (nationality) out.push(`whose nationality is ${nationality}`);
  const club = valuesWords(filters.club);
  if (club) out.push(`who play for ${club}`);
  const foot = valuesWords(filters.foot);
  if (foot) out.push(`who are ${foot.toLowerCase()}`);
  const age = filters.age as RangeActive | undefined;
  if (age) {
    if (age.min === -age.max) out.push(`within ${age.max} years of ${playerName}'s age`);
    else out.push(`aged from ${Math.abs(age.min)} years ${age.min < 0 ? "younger" : "older"} to ${Math.abs(age.max)} years ${age.max < 0 ? "younger" : "older"} than ${playerName}`);
  }
  const minutes = filters.minutes as RangeActive | undefined;
  if (minutes) out.push(`with at least ${minutes.min.toLocaleString()} minutes played`);
  return out;
}

export interface StatsExplainInput {
  playerName: string;
  /** The tile's category: "Passing", "Attacking"… */
  category: string;
  mode: "per90" | "total";
  /** The competition dropdown (full name) and season shown: "Premier League (England)", "26/27". */
  competition: string;
  season: string;
  /** Is the dropdown her league (not a cup / "All competitions")? */
  isLeague: boolean;
  filters: ActiveFilters;
  /** The Season chip in words: null = her season (untouched); "every season" when removed. */
  seasonChip: string | null;
  /** The League chip's default (her league), when it's untouched. */
  defaultLeague: string | null;
  goalkeeping: boolean;
  /** "N players match", or why there's no comparison. */
  group: { kind: "group"; count: number } | { kind: "none"; label: string } | null;
}

/** The Stats tile's explanation, as short paragraphs. */
export function explainStatsTile(x: StatsExplainInput): string[] {
  const numbers = x.mode === "total" ? "season totals" : "numbers per 90 minutes";
  const out = [`You're looking at ${x.playerName}'s ${x.category.toLowerCase()} stats (${numbers}) in the ${x.competition}, ${x.season}.`];
  if (!x.group) return out;
  if (x.group.kind === "none") {
    out.push(`There's no comparison right now: ${x.group.label.replace(/^No comparison( yet)?:\s*/i, "")}.`);
    return out;
  }
  const who = x.goalkeeping ? "goalkeepers" : "players";
  // A league's name without the "(England)" the picker adds (the line above says it plainly).
  const plain = (l: string) => l.replace(/\s*\([^)]*\)\s*$/, "");
  const leagues = selectedValuesOf(x.filters.league as CategoricalActive | undefined).map(plain);
  const from = x.isLeague
    ? `from the ${listWords(leagues.length ? leagues : x.defaultLeague ? [plain(x.defaultLeague)] : [x.competition])}`
    : `who played in the ${x.competition}`;
  const when = x.seasonChip ? ` in ${x.seasonChip}` : "";
  const filters = filterPhrases(x.filters, x.playerName);
  const rest = filters.length ? `, ${listWords(filters)}` : "";
  out.push(`Each bar compares ${x.playerName} with ${x.group.count.toLocaleString()} ${who} ${from}${when}${rest}.`);
  out.push(
    `How to read a bar: the dark bar is the ${x.competition}, ${x.season}, from its 5th to its 95th percentile. ` +
      `The light green band is those ${x.group.count.toLocaleString()} ${who}, lowest to highest. The dot is ${x.playerName}: hover it to see how they rank.`,
  );
  return out;
}

// --- shared pieces -------------------------------------------------------------------------

const per = (mode: "per90" | "total") => (mode === "total" ? "season totals" : "per 90 minutes");

/** "from the Premier League and Serie A" (the League chip's ticks). */
function fromLeagues(leagues: readonly string[]): string {
  return leagues.length ? ` from the ${listWords(leagues)}` : "";
}

/** The League chip's leagues, as ticked (one, several, or none). */
export function leaguesOf(filters: ActiveFilters): string[] {
  return selectedValuesOf(filters.league as CategoricalActive | undefined);
}

/** ", who play as Midfielder and who are left-footed" — or "". */
function filtersTail(filters: ActiveFilters, refName: string): string {
  const f = filterPhrases(filters, refName);
  return f.length ? `, ${listWords(f)}` : "";
}

/** A group module (Ranking, Filter, Comparison) with no group: why, from its own readout. */
function noGroupLine(label: string | null | undefined): string {
  const why = (label ?? "").replace(/^No (ranking|comparison|group)( yet)?:\s*/i, "");
  return why ? `There's nothing to show right now: ${why.charAt(0).toLowerCase()}${why.slice(1)}.` : "There's nothing to show right now.";
}

/** The chips of an event module (Shot Map, Pass Map, Heat Map, Passing Profile) in words. */
export interface EventChip {
  label: string;
  value: string;
}
/** An event module's active chips, as label + value ("Body Part", "not Head"; "Time", "0–45"). */
export function eventChipsOf(active: EventActive, defs: readonly EventFilterDef<never>[]): EventChip[] {
  const out: EventChip[] = [];
  for (const d of defs) {
    const v = active[d.key];
    if (v === undefined) continue;
    if (d.type === "range") {
      const r = v as { min: number; max: number };
      const fmt = d.format ?? ((n: number) => `${n}${d.unit ?? ""}`);
      out.push({ label: d.label, value: `${fmt(r.min)} to ${fmt(r.max)}` });
    } else {
      out.push({ label: d.label, value: isEventExclude(v) ? `not ${v.value}` : String(v) });
    }
  }
  return out;
}

function eventChipsLine(chips: readonly EventChip[], shown: number, total: number, noun: string): string | null {
  if (!chips.length) return null;
  const words = chips.map((c) => `${c.label.toLowerCase()} ${c.value}`);
  return `The filters (${listWords(words)}) leave ${shown.toLocaleString()} of the ${total.toLocaleString()} ${noun}.`;
}

// --- Bio --------------------------------------------------------------------------------

export function explainBio(x: { name: string; team: string | null; competition: string | null }): string[] {
  const where = x.team ? ` They play for ${x.team}${x.competition ? ` in the ${x.competition}` : ""}.` : "";
  return [
    `You're looking at ${x.name}'s player card: who they are, their age, height and weight.${where}`,
    "Last Appearance is the most recent match we have for them, and who it was against.",
    "The HOPS bar is Catalyst's overall rating for the player: the longer the bar, the higher the rating.",
  ];
}

export function explainBioTeam(name: string): string[] {
  return [
    `You're looking at ${name}'s team card: where they are in the table, their record, goals for and against, and their form (the last five results, newest on the right).`,
    "Last result and Next game are their most recent and coming league games.",
    "History shows where they finished in this league each season. Pick a player in the search box above to show a player again.",
  ];
}

export function explainBioTeamHistory(name: string): string[] {
  return [
    `You're looking at where ${name} finished in this league, one line per season, newest first, with their points and goal difference.`,
    "Drag a season out onto a League Table (that season, the team highlighted), a Matches (that season's games) or empty canvas (that season's team card). The Profile button at the top takes you back to their team card.",
  ];
}

// --- Ranking ---------------------------------------------------------------------------------

export interface RankingExplainInput {
  metric: string;
  mode: "per90" | "total";
  season: string;
  filters: ActiveFilters;
  refName: string;
  /** Players ranked; null = nothing to rank (`label` says why). */
  count: number | null;
  label: string | null;
  /** The player found with "Find player…", if any. */
  found: string | null;
  /** The Men's / Women's switch moved off the player's gender: the gender shown. */
  switchedTo?: boolean | null;
}

export function explainRanking(x: RankingExplainInput): string[] {
  if (x.count === null) return [`You're looking at a ranking of players by ${x.metric}.`, noGroupLine(x.label), ...switchedLine(x.switchedTo, x.refName)];
  const out = [
    `You're looking at ${x.count.toLocaleString()} players${fromLeagues(leaguesOf(x.filters))}${filtersTail(x.filters, x.refName)}, ranked by ${x.metric} (${per(x.mode)}) in ${x.season}, highest first.`,
    ...switchedLine(x.switchedTo, x.refName),
  ];
  if (x.found) out.push(`${x.found} is highlighted in the list.`);
  out.push("Use the chips to change who's in the list, and drag a player onto the canvas to open them.");
  return out;
}

// --- Filter ----------------------------------------------------------------------------------

export interface FilterExplainInput {
  season: string;
  mode: "per90" | "total";
  filters: ActiveFilters;
  refName: string;
  count: number | null;
  label: string | null;
  /** The stats narrowed with a slider (their names). */
  sliders: readonly string[];
  /** The Men's / Women's switch moved off the player's gender: the gender shown. */
  switchedTo?: boolean | null;
}

export function explainFilter(x: FilterExplainInput): string[] {
  if (x.count === null) return ["You're looking at a player search.", noGroupLine(x.label), ...switchedLine(x.switchedTo, x.refName)];
  const out = [
    `You're looking at a player search: ${x.count.toLocaleString()} players${fromLeagues(leaguesOf(x.filters))}${filtersTail(x.filters, x.refName)} match, in ${x.season}.`,
    ...switchedLine(x.switchedTo, x.refName),
  ];
  out.push(
    x.sliders.length
      ? `They're also narrowed by ${listWords(x.sliders)} with the sliders (${per(x.mode)}).`
      : "Drag a slider to keep only players within a range of a stat.",
  );
  out.push("Drag a player onto the canvas to open them.");
  return out;
}

// --- Stats Table ------------------------------------------------------------------------------

export interface StatsTableExplainInput {
  season: string;
  mode: "per90" | "total";
  filters: ActiveFilters;
  refName: string;
  count: number | null;
  label: string | null;
  /** The template's name ("Midfielder"), or null for the user's own columns. */
  template: string | null;
  columns: number;
  /** The sorted column's name and which way round. */
  sortedBy: string;
  ascending: boolean;
  /** The Men's / Women's switch moved off the player's gender: the gender shown (true = women's). */
  switchedTo: boolean | null;
}

/** The ? card's line when the Men's / Women's switch is off the player's gender, or nothing. */
export function switchedLine(switchedTo: boolean | null | undefined, refName: string): string[] {
  if (switchedTo === null || switchedTo === undefined) return [];
  return [`Switched to ${switchedTo ? "women's" : "men's"} leagues with the Men's / Women's button; ${refName} isn't in this group.`];
}

export function explainStatsTable(x: StatsTableExplainInput): string[] {
  if (x.count === null) return ["You're looking at a table of players' stats.", noGroupLine(x.label), ...switchedLine(x.switchedTo, x.refName)];
  const which = x.template ? `the ${x.template} template's ${x.columns} stats` : `${x.columns} stats you picked`;
  return [
    `You're looking at ${x.count.toLocaleString()} players${fromLeagues(leaguesOf(x.filters))}${filtersTail(x.filters, x.refName)} in ${x.season}, with ${which} side by side (${per(x.mode)}).`,
    ...switchedLine(x.switchedTo, x.refName),
    `Sorted by ${x.sortedBy}, ${x.ascending ? "lowest" : "highest"} first. Click a heading to sort by it; drag a heading's table icon out for a Ranking of that stat.`,
    "Use the chips to change who's in the table, the template or the column button to change the stats, and drag a player onto the canvas to open them.",
  ];
}

// --- Comparison -------------------------------------------------------------------------

export interface ComparisonExplainInput {
  x: string;
  y: string;
  size: string | null;
  /** "position", a stat's name, or null. */
  colour: string | null;
  season: string;
  mode: "per90" | "total";
  filters: ActiveFilters;
  refName: string;
  count: number;
  /** Why there's no group, or null. */
  noGroup: string | null;
  table: boolean;
  highlight: string | null;
}

export function explainComparison(x: ComparisonExplainInput): string[] {
  if (x.noGroup) return ["You're looking at a chart comparing players on two stats.", noGroupLine(x.noGroup)];
  const who = `${x.count.toLocaleString()} players${fromLeagues(leaguesOf(x.filters))}${filtersTail(x.filters, x.refName)}`;
  const out = x.table
    ? [`You're looking at a table of ${who}, in ${x.season}, with their ${x.x} and ${x.y}.`]
    : [`You're looking at ${who}, in ${x.season}. Each dot is one player: further right means more ${x.x}, higher up means more ${x.y}.`];
  const extras: string[] = [];
  if (x.size) extras.push(`a bigger dot means more ${x.size}`);
  if (x.colour === "position") extras.push("the colour shows their position");
  else if (x.colour) extras.push(`the colour shows ${x.colour}`);
  if (extras.length && !x.table) out.push(`${listWords(extras).charAt(0).toUpperCase()}${listWords(extras).slice(1)}.`);
  if (x.mode === "total") out.push("Stats are season totals, not per 90 minutes.");
  if (x.highlight) out.push(`${x.highlight} is highlighted.`);
  return out;
}

// --- Similarity -------------------------------------------------------------------------

export interface SimilarityExplainInput {
  name: string;
  season: string;
  template: string;
  filters: ActiveFilters;
  /** Why there's no group, or null. */
  noGroup: string | null;
  /** The League chip removed: nobody to compare with. */
  leagueEmpty: boolean;
  overlay: string | null;
}

export function explainSimilarity(x: SimilarityExplainInput): string[] {
  const out = [
    `You're looking at the players whose numbers look most like ${x.name}'s in ${x.season}, using the ${x.template} radar's stats.`,
  ];
  if (x.noGroup) return [...out, noGroupLine(x.noGroup)];
  if (x.leagueEmpty) return [...out, "Pick a league to see who's similar."];
  const leagues = leaguesOf(x.filters);
  out.push(`They come ${leagues.length ? fromLeagues(leagues).trim() : "from every league"}${filtersTail(x.filters, x.name)}.`);
  out.push(
    `Similarity is how close their numbers are to ${x.name}'s: 100% would be identical. A green arrow means they look better overall on these stats, a red one worse.`,
  );
  out.push(
    x.overlay
      ? `${x.overlay} is drawn over ${x.name} on the radar; the notes underneath say where they differ.`
      : `Click a player to draw them over ${x.name} on the radar.`,
  );
  return out;
}

// --- Radar -----------------------------------------------------------------------------------

export interface RadarExplainInput {
  name: string;
  season: string;
  /** "Full Back"; empty = none picked yet. */
  template: string;
  league: string | null;
  distribution: boolean;
  /** Why nothing is drawn, or null. */
  empty: string | null;
}

export function explainRadar(x: RadarExplainInput): string[] {
  const where = x.league ? ` in the ${x.league}` : "";
  if (!x.template) return [`You're looking at ${x.name}'s radar for ${x.season}.`, "Pick a position to draw it."];
  const out = [
    `You're looking at ${x.name}'s radar for ${x.season}${where}: their whole season, on the ${x.template} radar's stats.`,
  ];
  if (x.empty) return [...out, x.empty];
  out.push(
    x.distribution
      ? `Each row is one stat: the curve shows how every ${x.template}${where} is spread, and the mark is ${x.name}.`
      : `Each spoke is one stat. The middle is the lowest of every ${x.template}${where} that season, the edge the highest, so the further out, the better ${x.name} compares.`,
  );
  out.push("Where a lower number is better (like fouls), the spoke is turned round, so further out is still better.");
  return out;
}

export function explainRadarMerged(x: { players: readonly { name: string; season: string }[]; template: string; scaleFrom: string | null }): string[] {
  const names = x.players.map((p) => `${p.name} (${p.season})`);
  const out = [`You're comparing ${listWords(names)} on the ${x.template} radar. Each colour is one player's whole season.`];
  if (x.scaleFrom) {
    out.push(
      `The scale comes from every ${x.template} in the ${x.scaleFrom}, the first player's league and season: the middle is the lowest, the edge the highest.`,
    );
  }
  return out;
}

export function explainTeamRadar(x: {
  template: string;
  teams: readonly { name: string; league: string; season: string }[];
  leagues: number;
  distribution: boolean;
  edited: boolean;
}): string[] {
  const [first] = x.teams;
  if (!first) throw new Error("explainTeamRadar: no team");
  const out =
    x.teams.length === 1
      ? [`You're looking at ${first.name}'s ${x.template} radar for ${first.season}, per game, against the other teams in the ${first.league}.`]
      : [`You're comparing ${listWords(x.teams.map((t) => `${t.name} (${t.league}, ${t.season})`))} on the ${x.template} radar. Each colour is one team.`];
  out.push(
    x.distribution
      ? `Each row is one stat: the curve shows how every team is spread, and the mark is ${x.teams.length === 1 ? "the team" : "each team, in its colour"}. The better end is always on the right.`
      : x.template === "Defending"
        ? "Each spoke is what the team's opponents did against them (goals, shots, xG conceded…), turned round: the middle is the most any team allowed, the edge the least, so a bigger shape is better defending."
        : "Each spoke is one of the team's own numbers. The middle is the lowest team, the edge the highest, so the further out, the better it compares.",
  );
  out.push(
    x.leagues > 1
      ? "The spokes run across every team in those leagues, so the teams share one scale."
      : `The scale is every team in the ${first.league} that season.`,
  );
  out.push(
    x.teams.length === 1
      ? "The season dropdown picks the team's season, the template Attacking or Defending. Drop another team here to compare them (up to five), or pick a player in the search box to see them instead."
      : "Drop another team here to add it (up to five), ✕ to take one off; drag a name out for its team card.",
  );
  out.push(
    `The Distribution button switches to each stat's spread; the slider button adds or removes stats (${
      x.template === "Defending" ? "the opponents' numbers, turned round" : "the team's own numbers"
    }).${x.edited ? ` These are your own picks for ${x.template}; Reset there brings back its ten.` : ""}`,
  );
  return out;
}

// --- Stats tile with several players ----------------------------------------------------------

export function explainStatsMerged(x: {
  category: string;
  mode: "per90" | "total";
  players: readonly { name: string; season: string; league: string | null }[];
}): string[] {
  const names = x.players.map((p) => `${p.name} (${p.season})`);
  const leagues = [...new Set(x.players.map((p) => (p.league ? `the ${p.league} ${p.season}` : null)).filter((l): l is string => !!l))];
  const out = [`You're comparing ${listWords(names)}'s ${x.category.toLowerCase()} stats (${per(x.mode)}). Each dot is one player, in their colour.`];
  if (leagues.length) out.push(`Each bar is ${listWords(leagues)} combined, from the 5th to the 95th percentile.`);
  return out;
}

export function explainMatch(home: string, away: string): string[] {
  return [
    `You're looking at ${home} against ${away}: the league and date, the score, both sides' xG and shots, and each side's goals and red cards with their minutes, under its name (the home side first).`,
    "Search for another game in the box at the top: a team (its games), two teams (every meeting), a score (\"Arsenal 5-2\", or just \"5-2\") or a month (\"Arsenal October\"); empty, it suggests games from your canvas. Added from the side panel, it opens on the current player's club's latest league game. Drop another game here to show it instead, or a player or a team for their latest game. Drag the league and date line onto a player's Shot Map, Pass Map, Heat Map or Passing Profile to see only their part of this game; drag a team's name out for its team card.",
  ];
}

export function explainTeamStats(x: { team: string; group: string; season: string; count: number; side: "for" | "against"; hasAgainst: boolean }): string[] {
  return [
    x.side === "for"
      ? `You're looking at ${x.team}'s own numbers in ${x.season}, per game, against ${x.count.toLocaleString()} ${x.group}.`
      : `You're looking at what ${x.team}'s opponents did against them in ${x.season}, per game (shots conceded, passes allowed…), against ${x.count.toLocaleString()} ${x.group}.`,
    "The light band runs from the group's lowest to its highest; the dot is the team. Hover it for its percentile (a lower-is-better stat is turned round, so a high percentile is always good).",
    "The season dropdown picks the team's season. The Season and League chips pick who it's compared with: by default its own league that season. Another season, every season, or other leagues' teams too.",
    // Only where the category has opponents' numbers (Appearance has none, so no button).
    `${x.hasAgainst ? `The ${x.side === "for" ? "For" : "Against"} button switches between the team's own numbers and its opponents'. ` : ""}Pick a player in the search box to see them instead.`,
  ];
}

export function explainTeamStatsMerged(x: { side: "for" | "against"; hasAgainst: boolean; teams: { name: string; league: string; season: string }[] }): string[] {
  const who = x.teams.map((t) => `${t.name} (${t.league}, ${t.season})`).join(", ");
  const leagues = new Set(x.teams.map((t) => t.league)).size;
  return [
    x.side === "for"
      ? `You're comparing ${who}: each team's own numbers, per game.`
      : `You're comparing what opponents did against ${who}, per game (shots conceded, passes allowed…).`,
    leagues > 1
      ? "Each bar runs from the lowest to the highest of every team in those leagues, so the teams share one scale; a dot each."
      : "Each bar runs from the league's lowest to its highest; a dot each.",
    `Drop another team on the tile to add it (up to five), ✕ to take one off.${x.hasAgainst ? " The For / Against button switches them all." : ""}`,
  ];
}

// --- League Table, Match Results, Squad --------------------------------------------------------

export function explainLeagueTable(x: { competition: string; season: string }): string[] {
  return [
    `You're looking at the ${x.competition} table for ${x.season}.`,
    "It's in order of points; click a column heading to sort by it, or a team to see its squad. Drag a team out onto the canvas for its team card.",
    "Drop a team on the table to open its league, its row highlighted; drop a player for their club's.",
  ];
}

export function explainMatchResults(x: { competition: string; team: string | null; count: number }): string[] {
  const whose = x.team ? `${x.team}'s ` : "";
  return [
    `You're looking at ${whose}${x.count.toLocaleString()} results in the ${x.competition}, newest first.`,
    x.team ? "Pick All Teams to see every result in the league." : "Pick a team to see only their games.",
    "Drag a team's name out onto the canvas for its team card, or the score for the match; drop a team (or a player, for their club) here for its results.",
  ];
}

export function explainSquad(x: { team: string | null; competition: string | null; count: number }): string[] {
  const where = x.competition ? ` in the ${x.competition}` : "";
  return [
    `You're looking at ${x.team ?? "a club"}'s squad${where}: ${x.count.toLocaleString()} players, with their position, nationality, age and HOPS (Catalyst's overall player rating).`,
    "Click a column heading to sort by it, and drag a player onto the canvas to open them. Drag the club's name (beside Table) out for its team card, or drop a team (or a player, for their club) here for its squad.",
  ];
}

// --- Matches -----------------------------------------------------------------------------------

export function explainTeamMatches(x: { team: string; season: string; games: number; venue: "home" | "away" | null }): string[] {
  return [
    `You're looking at ${x.team}'s ${x.games.toLocaleString()} league games in ${x.season}, newest first.`,
    "Each bar is one game, coloured by the result (green a win, grey a draw, red a defeat), with the score and both sides' xG beside it. The dots are goals (green theirs, red the opponent's) and the squares red cards, at their minute. Hover for more.",
    x.venue
      ? `Their ${x.venue} games are highlighted; click All to see every game again.`
      : "Click Home or Away under the chart to pick those games out.",
    "Drag a game's bar out (onto empty canvas or a Bio) for the match. Pick a player in the search box to see their games instead.",
  ];
}

export function explainMatches(x: { name: string; season: string; games: number; dots: readonly string[] }): string[] {
  return [
    `You're looking at ${x.name}'s ${x.games.toLocaleString()} games in their league in ${x.season}, newest first.`,
    "Each row is one game: the bar shows when they were on the pitch, with lines at half-time and full-time.",
    x.dots.length
      ? `The dots are their ${listWords(x.dots.map((d) => d.toLowerCase()))}, at the minute they happened.`
      : "Turn on the buttons above (like Shots or Goals) to add dots at the minute they happened.",
  ];
}

// --- Shot Map, Pass Map, Heat Map, Passing Profile ----------------------------------------------

export interface EventExplainInput {
  name: string;
  competition: string;
  season: string;
  total: number;
  shown: number;
  chips: readonly EventChip[];
  /** A match / position / season dragged in from Matches, or zones from a Pass Map
   *  (live/event-focus.ts), or none. */
  focus?: { kind: "match" | "position" | "season" | "zone" | "passes"; label: string } | null;
  /** A map showing a team's season (not one match): its For / Against switch and players list. */
  teamSeason?: boolean;
}

/** A team's season on a map: what its two own controls do. */
function teamSeasonLine(noun: string, who: string): string {
  return `For is the team's own ${noun}, Against its opponents' in its games. The ${who} list shows who made them: untick players, or click a name for only theirs.`;
}

/** The line saying the map is narrowed by a drag from Matches (Ben, 2026-10-18). */
function focusLine(name: string, f: EventExplainInput["focus"]): string | null {
  if (!f) return null;
  if (f.kind === "passes") {
    return `These are only ${name}'s ${f.label.toLowerCase()}, dragged in from a Passing Report. ✕ the chip on the map for the whole season.`;
  }
  if (f.kind === "zone") {
    return `These are only ${name}'s passes that started in ${f.label}, dragged in from a Pass Map. ✕ the chip on the map for the whole season.`;
  }
  const what =
    f.kind === "match" ? `one match (${f.label})` : f.kind === "position" ? `${name}'s minutes at ${f.label}` : `the matches of ${f.label}`;
  return `These are only from ${what}, dragged in from the Matches module. ✕ the chip on the map for the whole season.`;
}

export function explainShotMap(x: EventExplainInput & { colour: string; table: boolean; zoomed: boolean }): string[] {
  const out = [`You're looking at ${x.name}'s ${x.total.toLocaleString()} shots in the ${x.competition}, ${x.season}.`];
  const narrowed = focusLine(x.name, x.focus);
  if (narrowed) out.push(narrowed);
  const chips = eventChipsLine(x.chips, x.shown, x.total, "shots");
  if (chips) out.push(chips);
  out.push(
    x.table
      ? "Each row is one shot."
      : `Each circle is one shot, where it was taken; the colour shows ${x.colour.toLowerCase()}. Click one to see it in detail.`,
  );
  if (x.zoomed) out.push("You've zoomed into one area of the pitch; only shots there are counted.");
  if (x.teamSeason) out.push(teamSeasonLine("shots", "Shooters"));
  return out;
}

export function explainPassMap(x: EventExplainInput & { colour: string; table: boolean; zoomed: boolean; zones?: number | null }): string[] {
  const out = [`You're looking at ${x.name}'s ${x.total.toLocaleString()} passes in the ${x.competition}, ${x.season}.`];
  const narrowed = focusLine(x.name, x.focus);
  if (narrowed) out.push(narrowed);
  const chips = eventChipsLine(x.chips, x.shown, x.total, "passes");
  if (chips) out.push(chips);
  out.push(
    x.table ? "Each row is one pass." : `Each line is one pass, from where it started to where it ended; the colour shows ${x.colour.toLowerCase()}.`,
  );
  if (x.zoomed) out.push("You've zoomed into one area of the pitch; only passes there are counted.");
  if (x.teamSeason) out.push(teamSeasonLine("passes", "Passers"));
  // Live zones (Ben, 2026-10-19): null = the grid is off.
  if (x.zones === 0) out.push("The pitch is split into zones; each number is how many passes started there. Click a zone to see only those (Shift-click for more).");
  if (x.zones) {
    out.push(
      `${x.zones === 1 ? "One zone is" : `${x.zones} zones are`} picked: the numbers above count only the passes that started there. Drag a picked zone onto a Heat Map to see where they ended.`,
    );
  }
  return out;
}

export function explainHeatMap(x: EventExplainInput & { action: string; noun: string }): string[] {
  const out = [
    `You're looking at where ${x.name}'s ${x.action.toLowerCase()} happened in the ${x.competition}, ${x.season}: ${x.total.toLocaleString()} ${x.noun}.`,
  ];
  const narrowed = focusLine(x.name, x.focus);
  if (narrowed) out.push(narrowed);
  const chips = eventChipsLine(x.chips, x.shown, x.total, x.noun);
  if (chips) out.push(chips);
  out.push("The brighter the colour, the more of them happened there.");
  return out;
}

export function explainPassingProfile(x: EventExplainInput & { colour: string; wedge: boolean }): string[] {
  const out = [`You're looking at ${x.name}'s ${x.total.toLocaleString()} passes in the ${x.competition}, ${x.season}.`];
  const narrowed = focusLine(x.name, x.focus);
  if (narrowed) out.push(narrowed);
  const chips = eventChipsLine(x.chips, x.shown, x.total, "passes");
  if (chips) out.push(chips);
  out.push(
    `Every pass is drawn from the same centre point, by its direction and length; forward is up. The colour shows ${x.colour.toLowerCase()}.`,
  );
  out.push(x.wedge ? "One direction is picked: only passes that way are shown. Click away to see them all." : "Click a wedge to see only passes in that direction.");
  return out;
}

// --- Action Map (Zone Finder), Notes ------------------------------------------------------------

export function explainZoneFinder(x: { noun: string; league: string; season: string; zone: boolean; count: number }): string[] {
  return [
    `You're looking at the ${x.noun} made in the ${x.league}, ${x.season}: a fixed sample of matches, not the live data. Each dot is one.`,
    x.zone
      ? `The list ranks the ${x.count.toLocaleString()} players by how many they made inside the box you drew. Click the pitch to clear the box.`
      : `Draw a box on the pitch to rank players by how many ${x.noun} they made there.`,
  ];
}

export function explainNotes(): string[] {
  return ["This is a space for your own notes. Type anything here; it stays on this dashboard."];
}

export function explainBioCareer(name: string): string[] {
  return [
    `You're looking at ${name}'s club career: one line per season, newest first, with the club they played most for, their appearances and minutes.`,
    "Click a season for every competition that season. Drag a season onto a module (Matches, a map, a Stats tile or Radar) to show them in that season.",
    "The Profile button at the top takes you back to their profile.",
  ];
}

// --- Passing Report (Ben, 2026-10-20) --------------------------------------------------------

export function explainPassingReport(x: {
  name: string;
  competition: string;
  season: string;
  passes: number;
  focus: { kind: "match" | "position" | "season" | "zone" | "passes"; label: string } | null;
  picked: string | null;
}): string[] {
  const out = [`${x.name}'s passing in the ${x.competition}, ${x.season}: ${x.passes.toLocaleString()} passes, worked out the same way as Catalyst's Passing page.`];
  const narrowed = focusLine(x.name, x.focus);
  if (narrowed) out.push(narrowed);
  out.push(
    "Actual vs expected compares the passes completed with what the xPass model expected from the same passes; PSAX is the difference, added up. Hard passes are under 50% likely to succeed, easy ones over 80%.",
  );
  out.push(
    "Each bar segment, band and dotted number is a set of passes: click it to highlight those passes on an open Pass Map, or drag it onto a Pass Map, Passing Profile or Heat Map to show only those.",
  );
  if (x.picked) out.push(`${x.picked} are picked. Click again to clear.`);
  return out;
}
